import { useState } from "react";
import { 
    ArrowRight, 
    ArrowLeft,
    CheckCircle, 
    XCircle,
    AlertTriangle,
    TrendingUp,
    Download,
    Calendar,
    Loader2,
    ShoppingBag,
    Mail,
    MessageSquare,
    Package,
    Star,
    FileText,
    Truck,
    BarChart3,
    Award,
    AlertCircle,
    Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import ServicesPromo from "./ServicesPromo";
import LeadCaptureGate from "./LeadCaptureGate";
import { downloadShopifyFixPlanPDF } from "./utils/generateShopifyPDF";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Questions for the Shopify optimization score
const QUESTIONS = [
    {
        id: "abandoned_cart_email",
        question: "Do you use abandoned cart email?",
        icon: Mail,
        description: "Automated emails to recover abandoned carts and lost sales",
        missingFeature: "Abandoned Cart Recovery",
        revenueImpact: "Recover 10-15% of abandoned carts"
    },
    {
        id: "sms_marketing",
        question: "Do you use SMS marketing?",
        icon: MessageSquare,
        description: "Text message campaigns for promotions and updates",
        missingFeature: "SMS Marketing Campaigns",
        revenueImpact: "98% open rate vs 20% email"
    },
    {
        id: "bundles",
        question: "Do you offer bundles?",
        icon: Package,
        description: "Product bundles or frequently bought together features",
        missingFeature: "Product Bundling",
        revenueImpact: "Increase AOV by 20-35%"
    },
    {
        id: "product_reviews",
        question: "Do you use product reviews?",
        icon: Star,
        description: "Customer reviews and ratings on product pages",
        missingFeature: "Social Proof & Reviews",
        revenueImpact: "Reviews increase conversions by 270%"
    },
    {
        id: "optimized_descriptions",
        question: "Do you have optimized product descriptions?",
        icon: FileText,
        description: "SEO-optimized, benefit-focused product descriptions",
        missingFeature: "Optimized Product Copy",
        revenueImpact: "Better SEO & higher conversions"
    },
    {
        id: "free_shipping",
        question: "Do you use free shipping thresholds?",
        icon: Truck,
        description: "Free shipping when customers spend above a certain amount",
        missingFeature: "Free Shipping Threshold",
        revenueImpact: "Increase AOV by 30%+"
    },
    {
        id: "conversion_tracking",
        question: "Do you track conversion rate?",
        icon: BarChart3,
        description: "Analytics to monitor and improve conversion rates",
        missingFeature: "Conversion Analytics",
        revenueImpact: "Data-driven optimization"
    }
];

const POINTS_PER_QUESTION = 10;
const MAX_SCORE = QUESTIONS.length * POINTS_PER_QUESTION;

// Get grade based on score
const getGrade = (score) => {
    if (score >= 60) return { letter: "A", color: "emerald", label: "Excellent" };
    if (score >= 50) return { letter: "B", color: "blue", label: "Good" };
    if (score >= 40) return { letter: "C", color: "yellow", label: "Average" };
    if (score >= 30) return { letter: "D", color: "orange", label: "Below Average" };
    return { letter: "F", color: "red", label: "Needs Work" };
};

// Get feedback based on score
const getFeedback = (score) => {
    if (score >= 60) {
        return {
            title: "Your store is well-optimized!",
            description: "You're using most key revenue features. Focus on the remaining gaps to maximize your potential."
        };
    } else if (score >= 40) {
        return {
            title: "Good foundation, room for growth",
            description: "Your store has some optimization in place, but there are clear opportunities to increase revenue."
        };
    } else {
        return {
            title: "Significant revenue opportunities",
            description: "Your store is missing several key features that could dramatically increase your sales."
        };
    }
};

export default function ShopifyOptimizationScore({ onBack, user, subscription }) {
    const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-7 = questions, 8 = results
    const [answers, setAnswers] = useState({});
    const [isCalculating, setIsCalculating] = useState(false);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const [isPDFDownloaded, setIsPDFDownloaded] = useState(false);

    // Check if user has Pro/Agency access
    const isPaidUser = subscription?.tier === 'pro' || subscription?.tier === 'agency' || subscription?.tier === 'premium' ||
                       user?.subscription_tier === 'pro' || user?.subscription_tier === 'agency' || user?.subscription_tier === 'premium';
    const isTestUser = user?.email === 'testpro@sitegradepro.com' || user?.user_id === 'test_pro_user_123';

    const handleAnswer = (questionId, answer) => {
        setAnswers(prev => ({ ...prev, [questionId]: answer }));
        
        // Auto-advance after short delay
        setTimeout(() => {
            if (currentStep < QUESTIONS.length) {
                setCurrentStep(currentStep + 1);
            } else {
                // Show results
                setIsCalculating(true);
                setTimeout(() => {
                    setIsCalculating(false);
                    setCurrentStep(QUESTIONS.length + 1);
                }, 1500);
            }
        }, 300);
    };

    const calculateScore = () => {
        return Object.values(answers).filter(a => a === true).length * POINTS_PER_QUESTION;
    };

    const getMissingFeatures = () => {
        return QUESTIONS.filter(q => answers[q.id] === false);
    };

    const handleUnlockFixPlan = async () => {
        // Pro/Agency users or test users get free PDF download
        if (isPaidUser || isTestUser) {
            handleDirectDownload();
            toast.success("PDF downloaded! (Pro benefit)");
            return;
        }

        // Free users go through Stripe checkout
        setIsCheckingOut(true);
        try {
            const response = await axios.post(`${API}/checkout`, {
                plan_id: "shopify_fix_plan",
                origin_url: window.location.origin,
                email: leadData?.email || null,
                success_url_params: `tool=shopify-score&payment_success=true&plan_id=shopify_fix_plan`
            });
            window.location.href = response.data.url;
        } catch (error) {
            console.error("Checkout failed:", error);
            toast.error("Failed to start checkout. Please try again.");
            setIsCheckingOut(false);
        }
    };

    // Direct PDF download for Pro users
    const handleDirectDownload = async () => {
        try {
            const grade = getGrade(calculateScore());
            await downloadShopifyFixPlanPDF({
                businessName: leadData?.name || "Your Store",
                websiteUrl: leadData?.website || "yourstore.myshopify.com",
                answers: {
                    abandoned_cart: answers.abandoned_cart_email,
                    sms_marketing: answers.sms_marketing,
                    product_bundles: answers.product_bundles,
                    product_reviews: answers.product_reviews,
                    optimized_descriptions: answers.optimized_descriptions,
                    free_shipping: answers.free_shipping,
                    conversion_tracking: answers.conversion_tracking
                },
                score: calculateScore(),
                maxScore: MAX_SCORE,
                grade: grade,
                leadData
            });
            setIsPDFDownloaded(true);
            toast.success("Your Shopify Fix Plan has been downloaded!");
        } catch (error) {
            console.error("PDF generation failed:", error);
            toast.error("Failed to generate PDF.");
        }
    };

    const handleHireExpert = () => {
        if (onBack) {
            onBack("consultation");
        }
    };

    const resetCalculator = () => {
        setCurrentStep(0);
        setAnswers({});
    };

    const score = calculateScore();
    const grade = getGrade(score);
    const feedback = getFeedback(score);
    const missingFeatures = getMissingFeatures();

    // Grade colors mapping
    const gradeColors = {
        emerald: {
            bg: "bg-emerald-500/10",
            border: "border-emerald-500/30",
            text: "text-emerald-400",
            gradient: "from-emerald-500 to-emerald-400"
        },
        blue: {
            bg: "bg-blue-500/10",
            border: "border-blue-500/30",
            text: "text-blue-400",
            gradient: "from-blue-500 to-blue-400"
        },
        yellow: {
            bg: "bg-yellow-500/10",
            border: "border-yellow-500/30",
            text: "text-yellow-400",
            gradient: "from-yellow-500 to-yellow-400"
        },
        orange: {
            bg: "bg-orange-500/10",
            border: "border-orange-500/30",
            text: "text-orange-400",
            gradient: "from-orange-500 to-orange-400"
        },
        red: {
            bg: "bg-red-500/10",
            border: "border-red-500/30",
            text: "text-red-400",
            gradient: "from-red-500 to-red-400"
        }
    };

    const currentGradeColors = gradeColors[grade.color];

    return (
        <div className="min-h-screen bg-[#0a0a0a]">
            {/* Subtle grid background */}
            <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <button 
                        onClick={() => onBack && onBack()}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        data-testid="back-to-main-btn"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span className="text-sm">Back to SiteGradePro</span>
                    </button>
                    {currentStep > 0 && currentStep <= QUESTIONS.length && (
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500">
                                {currentStep} of {QUESTIONS.length}
                            </span>
                            <div className="w-32 h-1 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-300"
                                    style={{ width: `${(currentStep / QUESTIONS.length) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center">
                <div className="w-full max-w-2xl">
                    
                    {/* Intro Screen */}
                    {currentStep === 0 && (
                        <div className="text-center animate-fade-in" data-testid="shopify-intro">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm mb-8">
                                <ShoppingBag className="h-4 w-4" />
                                Free Store Assessment
                            </div>
                            
                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                                Shopify Optimization Score
                            </h1>
                            
                            <p className="text-lg text-gray-400 mb-10 max-w-lg mx-auto leading-relaxed">
                                Answer 7 quick questions to discover your store's optimization 
                                grade and uncover missing revenue features.
                            </p>
                            
                            <Button
                                onClick={() => setCurrentStep(1)}
                                size="lg"
                                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-10 py-6 text-lg rounded-lg shadow-lg shadow-green-500/20"
                                data-testid="start-assessment-btn"
                            >
                                Start Assessment
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                            
                            <p className="text-sm text-gray-500 mt-6">
                                Takes less than 2 minutes • No store connection required
                            </p>
                        </div>
                    )}

                    {/* Question Screens */}
                    {currentStep >= 1 && currentStep <= QUESTIONS.length && (
                        <div className="animate-fade-in" data-testid={`question-${currentStep}`}>
                            {(() => {
                                const question = QUESTIONS[currentStep - 1];
                                const Icon = question.icon;
                                
                                return (
                                    <Card className="bg-[#111111] border-white/10 overflow-hidden">
                                        <CardContent className="p-8 md:p-12">
                                            {/* Question Icon */}
                                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/20 flex items-center justify-center mb-8">
                                                <Icon className="h-8 w-8 text-green-400" />
                                            </div>
                                            
                                            {/* Question Text */}
                                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                                                {question.question}
                                            </h2>
                                            <p className="text-gray-400 mb-10">
                                                {question.description}
                                            </p>
                                            
                                            {/* Answer Buttons */}
                                            <div className="flex flex-col sm:flex-row gap-4">
                                                <Button
                                                    onClick={() => handleAnswer(question.id, true)}
                                                    variant="outline"
                                                    size="lg"
                                                    className={`flex-1 py-6 text-lg font-medium transition-all ${
                                                        answers[question.id] === true
                                                            ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                                                            : 'border-white/20 text-white hover:bg-white/5 hover:border-white/40'
                                                    }`}
                                                    data-testid="answer-yes-btn"
                                                >
                                                    <CheckCircle className="mr-2 h-5 w-5" />
                                                    Yes
                                                </Button>
                                                <Button
                                                    onClick={() => handleAnswer(question.id, false)}
                                                    variant="outline"
                                                    size="lg"
                                                    className={`flex-1 py-6 text-lg font-medium transition-all ${
                                                        answers[question.id] === false
                                                            ? 'bg-red-500/20 border-red-500 text-red-400'
                                                            : 'border-white/20 text-white hover:bg-white/5 hover:border-white/40'
                                                    }`}
                                                    data-testid="answer-no-btn"
                                                >
                                                    <XCircle className="mr-2 h-5 w-5" />
                                                    No
                                                </Button>
                                            </div>
                                            
                                            {/* Navigation */}
                                            {currentStep > 1 && (
                                                <button
                                                    onClick={() => setCurrentStep(currentStep - 1)}
                                                    className="mt-8 text-sm text-gray-500 hover:text-white transition-colors flex items-center gap-1"
                                                >
                                                    <ArrowLeft className="h-3 w-3" />
                                                    Previous question
                                                </button>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })()}
                        </div>
                    )}

                    {/* Calculating Animation */}
                    {isCalculating && (
                        <div className="text-center animate-fade-in" data-testid="calculating-state">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/20 flex items-center justify-center mx-auto mb-8">
                                <Loader2 className="h-10 w-10 text-green-400 animate-spin" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-3">
                                Calculating Your Score...
                            </h2>
                            <p className="text-gray-400">
                                Analyzing your store optimization
                            </p>
                        </div>
                    )}

                    {/* Results Screen */}
                    {currentStep === QUESTIONS.length + 1 && !isCalculating && (
                        <div className="animate-fade-in" data-testid="shopify-results">
                            {/* Lead Capture Gate - Show before full results */}
                            {!isUnlocked ? (
                                <LeadCaptureGate
                                    score={score}
                                    maxScore={MAX_SCORE}
                                    toolName="Shopify Optimization Score"
                                    previewContent={
                                        <div className="space-y-2">
                                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${currentGradeColors.bg} ${currentGradeColors.text}`}>
                                                <Award className="h-5 w-5" />
                                                <span className="font-semibold">Grade: {grade.letter} - {grade.label}</span>
                                            </div>
                                            <p className="text-sm text-gray-500">Unlock to see full feature breakdown & recommendations</p>
                                        </div>
                                    }
                                    onUnlock={(data) => {
                                        setLeadData(data);
                                        setIsUnlocked(true);
                                    }}
                                />
                            ) : (
                            <>
                            {/* Grade Display */}
                            <Card className={`bg-[#111111] border ${currentGradeColors.border} overflow-hidden mb-6`}>
                                <CardContent className="p-8 md:p-12 text-center">
                                    {/* Grade Circle */}
                                    <div className="relative w-40 h-40 mx-auto mb-6">
                                        <svg className="w-full h-full transform -rotate-90">
                                            <circle
                                                cx="80"
                                                cy="80"
                                                r="70"
                                                stroke="rgba(255,255,255,0.1)"
                                                strokeWidth="8"
                                                fill="none"
                                            />
                                            <circle
                                                cx="80"
                                                cy="80"
                                                r="70"
                                                stroke={`url(#gradeGradient-${grade.color})`}
                                                strokeWidth="8"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeDasharray={`${(score / MAX_SCORE) * 440} 440`}
                                                className="transition-all duration-1000"
                                            />
                                            <defs>
                                                <linearGradient id={`gradeGradient-${grade.color}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                                    {grade.color === 'emerald' && <><stop offset="0%" stopColor="#10b981" /><stop offset="100%" stopColor="#34d399" /></>}
                                                    {grade.color === 'blue' && <><stop offset="0%" stopColor="#3b82f6" /><stop offset="100%" stopColor="#60a5fa" /></>}
                                                    {grade.color === 'yellow' && <><stop offset="0%" stopColor="#eab308" /><stop offset="100%" stopColor="#facc15" /></>}
                                                    {grade.color === 'orange' && <><stop offset="0%" stopColor="#f97316" /><stop offset="100%" stopColor="#fb923c" /></>}
                                                    {grade.color === 'red' && <><stop offset="0%" stopColor="#ef4444" /><stop offset="100%" stopColor="#f87171" /></>}
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <span className={`text-6xl font-bold ${currentGradeColors.text}`}>{grade.letter}</span>
                                            <span className="text-gray-500 text-sm">{grade.label}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Score */}
                                    <div className="mb-4">
                                        <span className="text-4xl font-bold text-white">{score}</span>
                                        <span className="text-gray-500 text-lg"> / {MAX_SCORE} points</span>
                                    </div>
                                    
                                    {/* Feedback */}
                                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${currentGradeColors.bg} ${currentGradeColors.text} mb-4`}>
                                        <Award className="h-5 w-5" />
                                        <span className="font-semibold">Optimization Grade: {grade.letter}</span>
                                    </div>
                                    
                                    <p className="text-gray-400 max-w-md mx-auto">
                                        {feedback.description}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Breakdown */}
                            <Card className="bg-[#111111] border-white/10 mb-6">
                                <CardContent className="p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                        <BarChart3 className="h-5 w-5 text-green-400" />
                                        Feature Breakdown
                                    </h3>
                                    <div className="space-y-3">
                                        {QUESTIONS.map((q) => {
                                            const isYes = answers[q.id] === true;
                                            const Icon = q.icon;
                                            return (
                                                <div 
                                                    key={q.id}
                                                    className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Icon className="h-4 w-4 text-gray-500" />
                                                        <span className="text-gray-300 text-sm">{q.question}</span>
                                                    </div>
                                                    {isYes ? (
                                                        <span className="flex items-center gap-1 text-emerald-400 text-sm">
                                                            <CheckCircle className="h-4 w-4" />
                                                            +10
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-1 text-red-400 text-sm">
                                                            <XCircle className="h-4 w-4" />
                                                            0
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Missing Revenue Features */}
                            {missingFeatures.length > 0 && (
                                <Card className="bg-[#111111] border-orange-500/30 mb-8">
                                    <CardContent className="p-6">
                                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                            <AlertCircle className="h-5 w-5 text-orange-400" />
                                            Missing Revenue Features
                                        </h3>
                                        <div className="space-y-4">
                                            {missingFeatures.map((feature) => {
                                                const Icon = feature.icon;
                                                return (
                                                    <div 
                                                        key={feature.id}
                                                        className="flex items-start gap-4 p-4 bg-orange-500/5 rounded-lg border border-orange-500/20"
                                                    >
                                                        <div className="w-10 h-10 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
                                                            <Icon className="h-5 w-5 text-orange-400" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="text-white font-medium">{feature.missingFeature}</h4>
                                                            <p className="text-sm text-orange-400 mt-1">
                                                                💡 {feature.revenueImpact}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Implemented Features (if any) */}
                            {missingFeatures.length < QUESTIONS.length && (
                                <Card className="bg-[#111111] border-emerald-500/30 mb-8">
                                    <CardContent className="p-6">
                                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                            <CheckCircle className="h-5 w-5 text-emerald-400" />
                                            Features You Have
                                        </h3>
                                        <div className="flex flex-wrap gap-2">
                                            {QUESTIONS.filter(q => answers[q.id] === true).map((feature) => (
                                                <span 
                                                    key={feature.id}
                                                    className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-sm text-emerald-400"
                                                >
                                                    {feature.missingFeature}
                                                </span>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* CTAs */}
                            <div className="space-y-4">
                                {/* Pro user badge */}
                                {(isPaidUser || isTestUser) && (
                                    <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm mb-2">
                                        <Crown className="h-4 w-4" />
                                        <span>Pro Benefit: Free PDF Download</span>
                                    </div>
                                )}
                                
                                {isPDFDownloaded ? (
                                    <>
                                        <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                                            <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                                            <p className="text-emerald-400 font-semibold">PDF Downloaded!</p>
                                        </div>
                                        <Button
                                            onClick={handleDirectDownload}
                                            size="lg"
                                            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg shadow-green-500/20"
                                        >
                                            <Download className="mr-2 h-5 w-5" />
                                            Download Again
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        onClick={handleUnlockFixPlan}
                                        disabled={isCheckingOut}
                                        size="lg"
                                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg shadow-green-500/20"
                                        data-testid="unlock-fix-plan-btn"
                                    >
                                        {isCheckingOut ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Processing...
                                            </>
                                        ) : (isPaidUser || isTestUser) ? (
                                            <>
                                                <Download className="mr-2 h-5 w-5" />
                                                Download Shopify Fix Plan
                                            </>
                                        ) : (
                                            <>
                                                <Download className="mr-2 h-5 w-5" />
                                                Unlock Detailed Shopify Fix Plan — $19
                                            </>
                                        )}
                                    </Button>
                                )}
                                
                                <Button
                                    onClick={handleHireExpert}
                                    variant="outline"
                                    size="lg"
                                    className="w-full border-white/20 text-white hover:bg-white/5 hover:border-white/40 py-6 text-lg rounded-lg"
                                    data-testid="hire-expert-btn"
                                >
                                    <Calendar className="mr-2 h-5 w-5" />
                                    Hire Implementation Expert
                                </Button>
                            </div>

                            {/* Reset */}
                            <button
                                onClick={resetCalculator}
                                className="w-full mt-6 text-sm text-gray-500 hover:text-white transition-colors"
                                data-testid="retake-assessment-btn"
                            >
                                Retake Assessment
                            </button>

                            {/* Services Promo */}
                            <ServicesPromo onBookService={(serviceId) => {
                                if (onBack) onBack("consultation");
                            }} />
                            </>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
